import { Button } from "@comps/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@comps/ui/command";
import { Input } from "@comps/ui/input";
import { Label } from "@comps/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { Textarea } from "@comps/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { TreeDataItem, TreeView } from "@comps/ui/tree-view";
import Field, { AggregationType, FieldType, validNumericAggregationByType } from "@dto/fields";
import { ObjectInfos, SharedObjectInfos } from "@dto/objects";
import { IS_EMPTY, IS_NOT_EMPTY, Operator, Path } from "@dto/records";
import { PrimitiveSelect } from "@app/filter/PrimiteSelect";
import { Braces, Link2, Trash2 } from "lucide-react";
import { checkFormula, fieldToPath, getOperatorLabel, getValidOperator, pathKey } from "@utils/model";
import { buildAllPathTrees, buildPathTree, fillWithNameToPathKeyMap, fillWithPathKeyToNameMap, getTreePathValue } from "@utils/tree";
import { conditionOperandPathKey, fillWithLinkFieldNameToPathKeyMap, fillWithLinkFieldPathKeyToNameMap } from "./formulaLinkOperands";
import { TW_AppDialogButton, TW_ButtonList, TW_FieldFormulaSectionCheckButton, TW_FieldFormulaSectionContent, TW_FieldFormulaSectionFieldPopover, TW_FieldFormulaSectionInsertButtons, TW_FieldFormulaSectionOperationPopover, TW_FieldFormulaSectionVariablePopover, TW_FieldFormulaTextarea, TW_FieldSelectorButton, TW_FieldSelectorListItem, TW_FieldSelectorListItemCurrentField, TW_FieldSelectorListItemNoFieldsFound, TW_FieldSelectorOperationDescription, TW_FieldSelectorOperationSymbol, TW_FieldSelectorVariableDescription, TW_FieldSelectorVariableSymbol, TW_FormulaAggregationLabel, TW_FormulaAggregationPathSelector, TW_FormulaAggregationPathSelectorItemSelected, TW_FormulaAggregationPathSelectorItemSelectedArrow, TW_FormulaAggregationPathSelectorItemSelectedName, TW_FormulaAggregationPathSelectorList, getWsButtonStyle, useHover } from "@utils/twClasses";
import { escapeRegex, getDefaultNumericAggregation } from "@utils/types";
import { matchesSearch } from "@utils/search";
import { ChevronDown, CircleCheck, Plus } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate, translateHtml } from "src/i18n/i18n";
import { useModelStore } from "src/store";

export interface FormulaEditorProps {
    formula: string | undefined;
    onFormulaChange: (formula: string) => void;
    objectKey: string | undefined,
    currentFieldKey?: string;
    fieldSelectorEnabled: boolean;
}

export function FormulaEditor({ formula, onFormulaChange, objectKey, currentFieldKey, fieldSelectorEnabled: fieldSelectorEnabled }: FormulaEditorProps) {
    const { t } = useTranslation();
    const selectedWorkspace = useModelStore((state) => state.selectedWorkspace);
    const [checkStatus, setCheckStatus] = useState<'pending' | 'valid' | 'invalid'>('pending');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
    const objectInfos = useModelStore((state) => state.objectInfos);
    const sharedObjectInfos = useModelStore((state) => state.sharedObjectInfos);
    const relationByKeys = useModelStore((state) => state.relationByKeys);
    const fieldByKeys = useModelStore((state) => state.fieldByKeys);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const objectByKeys = useModelStore((state) => state.objectByKeys);

    // var objectTree: TreeDataItem | null = useMemo(() => {
    //     if (selectedObjectInfos) {
    //         return buildPathTree(objectInfos, sharedObjectInfos, selectedObjectInfos.object._key, () => { return; });
    //     }
    //     return null;
    // }, [selectedObjectInfos, objectInfos, sharedObjectInfos]);

    var allObjectTrees: TreeDataItem[] | null = useMemo(() => {
        if (selectedObjectInfos) {
            return buildAllPathTrees(objectInfos, sharedObjectInfos, () => { return; });
        }
        return null;
    }, [selectedObjectInfos, objectInfos, sharedObjectInfos]);

    // Objects of the workspace and of the shared workspaces, for the LINK field
    // labels (the path trees only hold the fields which have a value)
    const allObjectInfos: ObjectInfos[] = useMemo(() => [
        ...(objectInfos ?? []),
        ...(sharedObjectInfos?.flatMap(s => s.objectInfos) ?? []),
    ], [objectInfos, sharedObjectInfos]);

    // Create a map of field path keys to field names for display
    const fieldPathKeyToName = useMemo(() => {
        if (!fieldSelectorEnabled) return new Map<string, string>();

        const map = new Map<string, string>();
        if (selectedObjectInfos?.fields) {
            selectedObjectInfos.fields.forEach((field) => {
                const path = fieldToPath(selectedObjectInfos.object._key, relationByKeys, fieldByKeys, field);
                if (path) {
                    const pathKeyStr = pathKey(path);
                    map.set(pathKeyStr, field.name);
                }
            });
        }

        if (allObjectTrees && objectByKeys && relationByKeys) {
            fillWithPathKeyToNameMap(allObjectTrees, map, objectByKeys, relationByKeys);
        }
        fillWithLinkFieldPathKeyToNameMap(allObjectInfos, map, relationByKeys, fieldByKeys);
        return map;
    }, [selectedObjectInfos, relationByKeys, fieldByKeys, allObjectTrees, allObjectInfos]);

    // Create a map of field names to field path keys for storage
    const fieldNameToPathKey = useMemo(() => {
        const map = new Map<string, string>();
        if (!fieldSelectorEnabled) return map;

        if (selectedObjectInfos?.fields) {
            selectedObjectInfos.fields.forEach((field) => {
                const path = fieldToPath(selectedObjectInfos.object._key, relationByKeys, fieldByKeys, field);
                if (path) {
                    const pathKeyStr = pathKey(path);
                    map.set(field.name, pathKeyStr);
                }
            });
        }

        
        if (allObjectTrees && objectByKeys && relationByKeys) {
            fillWithNameToPathKeyMap(allObjectTrees, map, objectByKeys, relationByKeys);
        }
        fillWithLinkFieldNameToPathKeyMap(allObjectInfos, map, relationByKeys, fieldByKeys);
        return map;
    }, [selectedObjectInfos, relationByKeys, fieldByKeys, allObjectTrees, allObjectInfos]);

    // Convert formula from path keys to field names for display
    const displayFormula = useMemo(() => {
        if (!formula) return "";

        let result = formula;
        if (!fieldSelectorEnabled) return result;

        fieldPathKeyToName.forEach((fieldName, pathKey) => {
            const regex = new RegExp(`\\{\\{${escapeRegex(pathKey)}\\}\\}`, 'g');
            result = result.replace(regex, `{{${fieldName}}}`);
        });
        return result;
    }, [formula, fieldPathKeyToName]);

    // Convert formula from field names to path keys for storage
    const convertToPathKeys = (displayFormula: string): string => {
        let result = displayFormula;
        if (!fieldSelectorEnabled) return result;

        fieldNameToPathKey.forEach((pathKey, fieldName) => {
            const regex = new RegExp(`\\{\\{${escapeRegex(fieldName)}\\}\\}`, 'g');
            result = result.replace(regex, `{{${pathKey}}}`);
        });
        return result;
    };

    const insertFieldAtCursor = (fieldName: string) => {
        if (!textareaRef.current) return;

        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentValue = displayFormula || "";

        const fieldWithBraces = `{{${fieldName}}}`;
        const newDisplayValue = currentValue.substring(0, start) + fieldWithBraces + currentValue.substring(end);

        // Convert to path keys before calling onFormulaChange
        const newPathKeyValue = convertToPathKeys(newDisplayValue);
        onFormulaChange(newPathKeyValue);

        // Reset check status and error message
        setCheckStatus('pending');
        setErrorMessage('');

        // Set cursor position after the inserted field
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + fieldWithBraces.length, start + fieldWithBraces.length);
        }, 200);
    };

    const insertOperationAtCursor = (operation: string) => {
        if (!textareaRef.current) return;

        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentValue = displayFormula || "";

        const newDisplayValue = currentValue.substring(0, start) + operation + currentValue.substring(end);

        // Convert to path keys before calling onFormulaChange
        const newPathKeyValue = convertToPathKeys(newDisplayValue);
        onFormulaChange(newPathKeyValue);

        // Reset check status and error message
        setCheckStatus('pending');
        setErrorMessage('');

        // Set cursor position after the inserted operation
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + operation.length, start + operation.length);
        }, 200);
    };

    const insertVariableAtCursor = (variable: string) => {
        if (!textareaRef.current) return;

        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentValue = displayFormula || "";

        const varWithBraces = `\${${variable}}`;
        const newDisplayValue = currentValue.substring(0, start) + varWithBraces + currentValue.substring(end);

        onFormulaChange(newDisplayValue);

        // Reset check status and error message
        setCheckStatus('pending');
        setErrorMessage('');

        // Set cursor position after the inserted operation
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + variable.length, start + variable.length);
        }, 200);
    };

    const insertAggregationAtCursor = (formulaString: string) => {
        if (!textareaRef.current) return;

        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentValue = displayFormula || "";

        const newDisplayValue = currentValue.substring(0, start) + formulaString + currentValue.substring(end);

        // convertToPathKeys is safe to call: {{:key}} path refs are left unchanged
        const newPathKeyValue = convertToPathKeys(newDisplayValue);
        onFormulaChange(newPathKeyValue);

        setCheckStatus('pending');
        setErrorMessage('');

        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + formulaString.length, start + formulaString.length);
        }, 200);
    };

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const displayValue = e.target.value;
        // Convert to path keys before calling onFormulaChange
        const pathKeyValue = convertToPathKeys(displayValue);
        onFormulaChange(pathKeyValue);

        // Reset check status and error message
        setCheckStatus('pending');
        setErrorMessage('');
    };

    const handleCheckFormula = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (!formula || !objectKey) return;

        // Reset check status and error message
        setCheckStatus('pending');
        setErrorMessage('');

        checkFormula(selectedWorkspace?._key, objectKey, currentFieldKey, formula, () => {
            setCheckStatus('valid');
        }, (errorI18n, _, errorParams) => {
            setCheckStatus('invalid');
            setErrorMessage(translate(t, errorI18n, errorParams));
        });
    };

    const getCheckIconColor = () => {
        switch (checkStatus) {
            case 'valid': return 'text-green-500';
            case 'invalid': return 'text-red-500';
            default: return 'text-gray-400';
        }
    };

    const scrollStyle = {
        scrollbarColor: wsColor + " " + wsLightColor
    };
    return (
        <div className={TW_FieldFormulaSectionContent} style={scrollStyle}>
            <div className={TW_FieldFormulaSectionInsertButtons}>
                {fieldSelectorEnabled && (
                    <FieldSelectorPopover
                        fields={selectedObjectInfos?.fields || []}
                        onFieldSelect={insertFieldAtCursor}
                        currentFieldKey={currentFieldKey}
                    />
                )}
                <OperationSelectorPopover
                    onOperationSelect={insertOperationAtCursor}
                />
                <VariableSelectorPopover
                    onVariableSelect={insertVariableAtCursor}
                />
                <AggregationSelectorPopover onAggregationSelect={insertAggregationAtCursor} />
            </div>
            <Textarea
                ref={textareaRef}
                value={displayFormula}
                onChange={handleTextareaChange}
                className={TW_FieldFormulaTextarea}
                style={scrollStyle}
            />
            <div className="w-full flex justify-end gap-2 items-center">
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <CircleCheck className={`h-4 w-4 ${getCheckIconColor()}`} />
                        </TooltipTrigger>
                        {checkStatus === 'invalid' && errorMessage && (
                            <TooltipContent>
                                {errorMessage}
                            </TooltipContent>
                        )}
                    </Tooltip>
                </TooltipProvider>
                <Button
                    onClick={handleCheckFormula}
                    className={TW_FieldFormulaSectionCheckButton + (!formula ? ' opacity-50 cursor-not-allowed' : '')}
                    disabled={!formula}
                >
                    {t("field.formula.check")}
                </Button>
            </div>
        </div>
    );
}

interface FieldSelectorPopoverProps {
    fields: Field[];
    onFieldSelect: (fieldName: string) => void;
    currentFieldKey?: string;
}

function FieldSelectorPopover({ fields, onFieldSelect, currentFieldKey }: FieldSelectorPopoverProps) {
    const { t } = useTranslation();
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Filter fields based on search term
    const filteredFields = useMemo(() => {
        return fields.filter(field => matchesSearch(field.name, searchTerm));
    }, [fields, searchTerm]);

    const handleFieldSelect = (fieldName: string) => {
        onFieldSelect(fieldName);
        setIsPopoverOpen(false);
        setSearchTerm("");
    };

    const scrollStyle = {
        scrollbarColor: wsColor + " " + wsLightColor
    };

    return (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal>
            <PopoverTrigger className={TW_FieldSelectorButton}>
                <Plus className="mr-2 h-4 w-4" />
                {t("field.formula.select.field.placeholder")}
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
                <div className="p-3 border-b">
                    <Input
                        placeholder={t("field.formula.search.placeholder")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full"
                    />
                </div>
                <div className={TW_FieldFormulaSectionFieldPopover} style={scrollStyle}>
                    {filteredFields.length > 0 ? (
                        filteredFields.map((field) => {
                            const isCurrentField = field._key === currentFieldKey;
                            return (
                                <Button
                                    key={field._key}
                                    variant="ghost"
                                    className={`${TW_FieldSelectorListItem} ${isCurrentField ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    onClick={() => !isCurrentField && handleFieldSelect(field.name)}
                                    disabled={isCurrentField}
                                >
                                    {field.name}
                                    {isCurrentField && <span className={TW_FieldSelectorListItemCurrentField}>{t("field.formula.current.field")}</span>}
                                </Button>
                            );
                        })
                    ) : (
                        <div className={TW_FieldSelectorListItemNoFieldsFound}>
                            {t("field.formula.no.fields.found")}
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

interface OperationSelectorPopoverProps {
    onOperationSelect: (operation: string) => void;
}

function OperationSelectorPopover({ onOperationSelect }: OperationSelectorPopoverProps) {
    const { t } = useTranslation();
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Define available operations
    const operations = [
        { name: '+ (addition)', symbol: '+', description: t("field.formula.operation.description.addition"), fullDescription: translateHtml(t, "field.formula.operation.full.description.addition") },
        { name: '- (substraction)', symbol: '-', description: t("field.formula.operation.description.subtraction"), fullDescription: translateHtml(t, "field.formula.operation.full.description.subtraction") },
        { name: '* (multiplication)', symbol: '*', description: t("field.formula.operation.description.multiplication"), fullDescription: translateHtml(t, "field.formula.operation.full.description.multiplication") },
        { name: '/ (division)', symbol: '/', description: t("field.formula.operation.description.division"), fullDescription: translateHtml(t, "field.formula.operation.full.description.division") },
        { name: '() (grouping)', symbol: '()', description: t("field.formula.operation.description.grouping"), fullDescription: translateHtml(t, "field.formula.operation.full.description.grouping") },
        { name: 'CONCAT()', symbol: 'CONCAT(', description: t("field.formula.operation.description.concat"), fullDescription: translateHtml(t, "field.formula.operation.full.description.concat") },
        { name: 'LENGTH()', symbol: 'LENGTH(', description: t("field.formula.operation.description.length"), fullDescription: translateHtml(t, "field.formula.operation.full.description.length") },
        { name: 'CONTAINS()', symbol: 'CONTAINS(', description: t("field.formula.operation.description.contains"), fullDescription: translateHtml(t, "field.formula.operation.full.description.contains") },
        { name: 'REPLACE()', symbol: 'REPLACE(', description: t("field.formula.operation.description.replace"), fullDescription: translateHtml(t, "field.formula.operation.full.description.replace") },
        { name: 'STRING()', symbol: 'STRING(', description: t("field.formula.operation.description.string"), fullDescription: translateHtml(t, "field.formula.operation.full.description.string") },
        { name: 'START_DATE()', symbol: 'START_DATE(', description: t("field.formula.operation.description.start_date"), fullDescription: translateHtml(t, "field.formula.operation.full.description.start_date") },
        { name: 'END_DATE()', symbol: 'END_DATE(', description: t("field.formula.operation.description.end_date"), fullDescription: translateHtml(t, "field.formula.operation.full.description.end_date") },
        { name: 'ADD_DAYS()', symbol: 'ADD_DAYS(', description: t("field.formula.operation.description.add_days"), fullDescription: translateHtml(t, "field.formula.operation.full.description.add_days") },
        { name: 'DAY_OF_WEEK()', symbol: 'DAY_OF_WEEK(', description: t("field.formula.operation.description.day_of_week"), fullDescription: translateHtml(t, "field.formula.operation.full.description.day_of_week") },
        { name: 'DAY_OF_MONTH()', symbol: 'DAY_OF_MONTH(', description: t("field.formula.operation.description.day_of_month"), fullDescription: translateHtml(t, "field.formula.operation.full.description.day_of_month") },
        { name: 'DAY_OF_YEAR()', symbol: 'DAY_OF_YEAR(', description: t("field.formula.operation.description.day_of_year"), fullDescription: translateHtml(t, "field.formula.operation.full.description.day_of_year") },
        { name: 'WEEK()', symbol: 'WEEK(', description: t("field.formula.operation.description.week"), fullDescription: translateHtml(t, "field.formula.operation.full.description.week") },
        { name: 'MONTH()', symbol: 'MONTH(', description: t("field.formula.operation.description.month"), fullDescription: translateHtml(t, "field.formula.operation.full.description.month") },
        { name: 'QUARTER()', symbol: 'QUARTER(', description: t("field.formula.operation.description.quarter"), fullDescription: translateHtml(t, "field.formula.operation.full.description.quarter") },
        { name: 'YEAR()', symbol: 'YEAR(', description: t("field.formula.operation.description.year"), fullDescription: translateHtml(t, "field.formula.operation.full.description.year") },
        { name: 'WEEK_PERIOD()', symbol: 'WEEK_PERIOD(', description: t("field.formula.operation.description.week_period"), fullDescription: translateHtml(t, "field.formula.operation.full.description.week_period") },
        { name: 'MONTH_PERIOD()', symbol: 'MONTH_PERIOD(', description: t("field.formula.operation.description.month_period"), fullDescription: translateHtml(t, "field.formula.operation.full.description.month_period") },
        { name: 'QUARTER_PERIOD()', symbol: 'QUARTER_PERIOD(', description: t("field.formula.operation.description.quarter_period"), fullDescription: translateHtml(t, "field.formula.operation.full.description.quarter_period") },
        { name: 'YEAR_PERIOD()', symbol: 'YEAR_PERIOD(', description: t("field.formula.operation.description.year_period"), fullDescription: translateHtml(t, "field.formula.operation.full.description.year_period") },
        { name: 'PERIOD()', symbol: 'PERIOD(', description: t("field.formula.operation.description.period"), fullDescription: translateHtml(t, "field.formula.operation.full.description.period") },
        { name: 'ADD_WEEKS()', symbol: 'ADD_WEEKS(', description: t("field.formula.operation.description.add_weeks"), fullDescription: translateHtml(t, "field.formula.operation.full.description.add_weeks") },
        { name: 'ADD_MONTHS()', symbol: 'ADD_MONTHS(', description: t("field.formula.operation.description.add_months"), fullDescription: translateHtml(t, "field.formula.operation.full.description.add_months") },
        { name: 'ADD_QUARTERS()', symbol: 'ADD_QUARTERS(', description: t("field.formula.operation.description.add_quarters"), fullDescription: translateHtml(t, "field.formula.operation.full.description.add_quarters") },
        { name: 'ADD_YEARS()', symbol: 'ADD_YEARS(', description: t("field.formula.operation.description.add_years"), fullDescription: translateHtml(t, "field.formula.operation.full.description.add_years") },
        { name: 'MIN()', symbol: 'MIN(', description: t("field.formula.operation.description.min"), fullDescription: translateHtml(t, "field.formula.operation.full.description.min") },
        { name: 'MAX()', symbol: 'MAX(', description: t("field.formula.operation.description.max"), fullDescription: translateHtml(t, "field.formula.operation.full.description.max") },
        { name: 'IF()', symbol: 'IF(', description: t("field.formula.operation.description.if"), fullDescription: translateHtml(t, "field.formula.operation.full.description.if") },
        // Conditions: usable alone in a checkbox field, or inside IF()
        { name: '== != < <= > >= (comparison)', symbol: '== ', description: t("field.formula.operation.description.comparison"), fullDescription: translateHtml(t, "field.formula.operation.full.description.comparison") },
        { name: 'AND', symbol: ' AND ', description: t("field.formula.operation.description.and"), fullDescription: translateHtml(t, "field.formula.operation.full.description.and") },
        { name: 'OR', symbol: ' OR ', description: t("field.formula.operation.description.or"), fullDescription: translateHtml(t, "field.formula.operation.full.description.or") },
        { name: 'NOT', symbol: 'NOT ', description: t("field.formula.operation.description.not"), fullDescription: translateHtml(t, "field.formula.operation.full.description.not") },
        { name: 'true / false', symbol: 'true', description: t("field.formula.operation.description.boolean"), fullDescription: translateHtml(t, "field.formula.operation.full.description.boolean") },
        { name: 'IS_EMPTY / IS_NOT_EMPTY', symbol: ' IS_EMPTY', description: t("field.formula.operation.description.is_empty"), fullDescription: translateHtml(t, "field.formula.operation.full.description.is_empty") },
    ];

    // Filter operations based on search term
    const filteredOperations = useMemo(() => {
        return operations.filter(operation =>
            operation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            operation.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
            operation.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [operations, searchTerm]);

    const handleOperationSelect = (operation: string) => {
        onOperationSelect(operation);
        setIsPopoverOpen(false);
        setSearchTerm("");
    };

    const scrollStyle = {
        scrollbarColor: wsColor + " " + wsLightColor
    };

    return (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal>
            <PopoverTrigger className={TW_FieldSelectorButton}>
                <Plus className="mr-2 h-4 w-4" />
                {t("field.formula.select.operation.placeholder")}
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
                <div className="p-3 border-b">
                    <Input
                        placeholder={t("field.formula.search.operation.placeholder")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full"
                    />
                </div>
                <div className={TW_FieldFormulaSectionOperationPopover} style={scrollStyle}>
                    {filteredOperations.length > 0 ? (
                        filteredOperations.map((operation) => (
                            <>
                                <TooltipProvider key={operation.symbol + ".name"}>
                                    <Tooltip>
                                        <TooltipTrigger key={"operation.symbol." + operation.symbol} className={TW_FieldSelectorOperationSymbol} onClick={() => handleOperationSelect(operation.symbol)}>
                                            {operation.name}
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {operation.fullDescription}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider key={operation.symbol + ".description"}>
                                    <Tooltip>
                                        <TooltipTrigger key={"operation.description." + operation.symbol} className={TW_FieldSelectorOperationDescription} onClick={() => handleOperationSelect(operation.symbol)}>
                                            {operation.description}
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {operation.fullDescription}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </>
                        ))
                    ) : (
                        <div className={TW_FieldSelectorListItemNoFieldsFound}>
                            {t("field.formula.operation.no.operations.found")}
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

interface VariableSelectorPopoverProps {
    onVariableSelect: (variable: string) => void;
}

function VariableSelectorPopover({ onVariableSelect }: VariableSelectorPopoverProps) {
    const { t } = useTranslation();
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Define available variables
    const variables = [
        { symbol: 'TODAY', description: t("field.formula.variable.description.today"), fullDescription: translateHtml(t, "field.formula.variable.full.description.today") },
        { symbol: 'THIS_WEEK', description: t("field.formula.variable.description.this_week"), fullDescription: translateHtml(t, "field.formula.variable.full.description.this_week") },
        { symbol: 'THIS_MONTH', description: t("field.formula.variable.description.this_month"), fullDescription: translateHtml(t, "field.formula.variable.full.description.this_month") },
        { symbol: 'THIS_QUARTER', description: t("field.formula.variable.description.this_quarter"), fullDescription: translateHtml(t, "field.formula.variable.full.description.this_quarter") },
        { symbol: 'THIS_YEAR', description: t("field.formula.variable.description.this_year"), fullDescription: translateHtml(t, "field.formula.variable.full.description.this_year") },
    ];

    // Filter operations based on search term
    const filteredVariables = useMemo(() => {
        return variables.filter(variable =>
            variable.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
            variable.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [variables, searchTerm]);

    const handleVariableSelect = (variable: string) => {
        onVariableSelect(variable);
        setIsPopoverOpen(false);
        setSearchTerm("");
    };

    const scrollStyle = {
        scrollbarColor: wsColor + " " + wsLightColor
    };

    return (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal>
            <PopoverTrigger className={TW_FieldSelectorButton}>
                <Plus className="mr-2 h-4 w-4" />
                {t("field.formula.select.variable.placeholder")}
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
                <div className="p-3 border-b">
                    <Input
                        placeholder={t("field.formula.search.variable.placeholder")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full"
                    />
                </div>
                <div className={TW_FieldFormulaSectionVariablePopover} style={scrollStyle}>
                    {filteredVariables.length > 0 ? (
                        filteredVariables.map((variable) => (
                            <>
                                <TooltipProvider key={variable.symbol + ".name"}>
                                    <Tooltip>
                                        <TooltipTrigger key={"variable.symbol." + variable.symbol} className={TW_FieldSelectorVariableSymbol} onClick={() => handleVariableSelect(variable.symbol)}>
                                            {variable.symbol}
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {variable.fullDescription}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider key={variable.symbol + ".description"}>
                                    <Tooltip>
                                        <TooltipTrigger key={"variable.description." + variable.symbol} className={TW_FieldSelectorVariableDescription} onClick={() => handleVariableSelect(variable.symbol)}>
                                            {variable.description}
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {variable.fullDescription}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </>
                        ))
                    ) : (
                        <div className={TW_FieldSelectorListItemNoFieldsFound}>
                            {t("field.formula.variable.no.variables.found")}
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}


type FunctionMode = 'AGGR' | 'LOOKUP';
const AggrFunctionMode : FunctionMode = 'AGGR';
const LookupFunctionMode : FunctionMode = 'LOOKUP';

type ConditionRightType = 'value' | 'field' | 'formula';

interface ConditionRow {
    id: string;
    leftFieldKey: string | null;
    operator: Operator;
    rightType: ConditionRightType;
    rightValue: string;
}

const VALID_AGGREGATIONS = [
    AggregationType.COUNT,
    AggregationType.COUNT_DISTINCT,
    AggregationType.SUM,
    AggregationType.AVG,
    AggregationType.MIN,
    AggregationType.MAX,
];

const LOOKUP_FIRST = 'FIRST';

function operatorToConditionText(op: Operator): string {
    switch (op) {
        case 'EQUAL': return '==';
        case 'NOT_EQUAL': return '!=';
        case 'INF': return '<';
        case 'SUP': return '>';
        case 'INF_EQUAL': return '<=';
        case 'SUP_EQUAL': return '>=';
        default: return '==';
    }
}

// Convert rightValue string → typed value for PrimitiveSelect
function parseConditionValue(rightValue: string, fieldType: FieldType | undefined): any {
    if (!fieldType) return rightValue || undefined;
    if (fieldType === FieldType.Boolean) return rightValue === 'true';
    if (fieldType === FieldType.Number || fieldType === FieldType.Decimal ||
        fieldType === FieldType.Devise || fieldType === FieldType.Percent) {
        const n = Number(rightValue);
        return isNaN(n) ? undefined : n;
    }
    return rightValue || undefined;
}

// Flat tree: all objects + their direct (non-link) fields, no relation traversal.
// IDs use "objectKey/fieldKey" format so getTreePathValue builds a path with no edges.
function buildLookupTree(
    objectInfos: ObjectInfos[] | undefined,
    sharedObjectInfos: SharedObjectInfos[] | undefined,
    onPathClicked: (id: string) => void
): TreeDataItem[] {
    const allInfos: ObjectInfos[] = [
        ...(objectInfos ?? []),
        ...(sharedObjectInfos?.flatMap(s => s.objectInfos) ?? []),
    ];
    return allInfos.map(oi => ({
        id: oi.object._key,
        name: oi.object.name,
        children: (oi.fields ?? [])
            .filter(f => f.type !== FieldType.Link)
            .map(f => ({
                id: oi.object._key + "/" + f._key,
                name: f.name,
                children: undefined,
                onClick: () => onPathClicked(oi.object._key + "/" + f._key),
            })),
    }));
}

interface AggregationSelectorPopoverProps {
    onAggregationSelect: (formula: string) => void;
}

function AggregationSelectorPopover({ onAggregationSelect }: AggregationSelectorPopoverProps) {
    const { t } = useTranslation();
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [functionMode, setFunctionMode] = useState<FunctionMode>(AggrFunctionMode);
    const [selectedPath, setSelectedPath] = useState<Path | undefined>(undefined);
    const [selectedPathName, setSelectedPathName] = useState<string | undefined>(undefined);
    const [selectedAggregation, setSelectedAggregation] = useState<string | undefined>(undefined);
    const [conditions, setConditions] = useState<ConditionRow[]>([]);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
    const confirmHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

    const objectInfos = useModelStore(state => state.objectInfos);
    const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const objectByKeys = useModelStore(state => state.objectByKeys);
    const relationByKeys = useModelStore(state => state.relationByKeys);
    const fieldByKeys = useModelStore(state => state.fieldByKeys);

    const useHoverAggrButton = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
    const useHoverLookupButton = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

    const scrollStyle = { scrollbarColor: wsColor + " " + wsLightColor };

    // Left-side condition fields: destination object's fields (end of selected path)
    const destFields = useMemo(() => {
        if (!selectedPath?.object) return [];
        const allInfos = [
            ...(objectInfos ?? []),
            ...(sharedObjectInfos?.flatMap(s => s.objectInfos) ?? []),
        ];
        return allInfos.find(oi => oi.object._key === selectedPath.object)?.fields ?? [];
    }, [selectedPath, objectInfos, sharedObjectInfos]);

    // Right-side condition fields when type = 'field': fields of the object being edited
    const currentObjectFields = useMemo(
        () => selectedObjectInfos?.fields ?? [],
        [selectedObjectInfos]
    );

    const selectedField = selectedPath?.field ? fieldByKeys?.get(selectedPath.field) : undefined;
    const numericOptions = validNumericAggregationByType(selectedField?.type, selectedField?.typeItems)
        .filter(a => VALID_AGGREGATIONS.includes(a));

    const currentAggregationOptions: string[] = selectedPath
        ? functionMode === LookupFunctionMode
            ? [LOOKUP_FIRST, ...numericOptions.map(a => a.toString())]
            : numericOptions.map(a => a.toString())
        : [];

    const isSubmitDisabled = !selectedPath || !selectedAggregation;

    function resetState() {
        setSelectedPath(undefined);
        setSelectedPathName(undefined);
        setSelectedAggregation(undefined);
        setConditions([]);
        setFunctionMode(AggrFunctionMode);
    }

    function onCancel() {
        resetState();
        setIsPopoverOpen(false);
    }

    function buildConditionString(): string {
        const currentObjKey = selectedObjectInfos?.object._key ?? '';
        const parts = conditions
            .filter(c => c.leftFieldKey && (
                c.operator === IS_EMPTY ||
                c.operator === IS_NOT_EMPTY ||
                c.rightValue.trim()
            ))
            .map(c => {
                // A LINK field is referenced by its link column in a LOOKUP (the
                // looked-up object is the root of the lookup query)
                const leftField = destFields.find(f => f._key === c.leftFieldKey);
                const left = functionMode === LookupFunctionMode && leftField
                    ? `{{${conditionOperandPathKey(selectedPath!.object, leftField, relationByKeys, fieldByKeys)}}}`
                    : `{{:${selectedPath!.object}/${c.leftFieldKey}}}`;
                if (c.operator === IS_NOT_EMPTY) return left;   // standalone = truthy check
                if (c.operator === IS_EMPTY) return null;       // not representable in grammar
                const op = operatorToConditionText(c.operator);
                let right: string;
                if (c.rightType === 'field') {
                    const rightField = currentObjectFields.find(f => f._key === c.rightValue);
                    right = rightField
                        ? `{{${conditionOperandPathKey(currentObjKey, rightField, relationByKeys, fieldByKeys)}}}`
                        : `{{:${currentObjKey}/${c.rightValue}}}`;
                } else if (c.rightType === 'formula') {
                    right = c.rightValue;
                } else {
                    const num = Number(c.rightValue);
                    if (!isNaN(num)) {
                        right = c.rightValue;
                    } else if (c.rightValue === 'true' || c.rightValue === 'false') {
                        right = c.rightValue;
                    } else {
                        right = `"${c.rightValue}"`;
                    }
                }
                return `${left} ${op} ${right}`;
            })
            .filter(Boolean) as string[];
        return parts.join(' AND ');
    }

    function onSubmit() {
        if (!selectedPath || !selectedAggregation) return;
        const pathKeyStr = pathKey(selectedPath);
        const condStr = buildConditionString();
        let formula: string;
        if (functionMode === AggrFunctionMode) {
            formula = condStr
                ? `${selectedAggregation}({{${pathKeyStr}}}, ${condStr})`
                : `${selectedAggregation}({{${pathKeyStr}}})`;
        } else {
            formula = condStr
                ? `LOOKUP({{${pathKeyStr}}}, ${selectedAggregation}, ${condStr})`
                : `LOOKUP({{${pathKeyStr}}}, ${selectedAggregation})`;
        }
        onAggregationSelect(formula);
        setIsPopoverOpen(false);
        resetState();
    }

    function onPathClick(id: string) {
        if (!objectByKeys || !relationByKeys) {
            setSelectedPath(undefined);
            return;
        }
        const treePathValue = getTreePathValue(id, objectByKeys, relationByKeys);
        if (treePathValue) {
            const field = fieldByKeys?.get(treePathValue.path.field);
            let defaultAggr: string | undefined;
            if (functionMode === LookupFunctionMode) {
                defaultAggr = LOOKUP_FIRST;
            } else {
                const opts = validNumericAggregationByType(field?.type, field?.typeItems)
                    .filter(a => VALID_AGGREGATIONS.includes(a));
                const def = getDefaultNumericAggregation(field);
                defaultAggr = def && opts.includes(def) ? def.toString() : undefined;
            }
            setSelectedPath(treePathValue.path);
            setSelectedPathName(treePathValue.name);
            setSelectedAggregation(defaultAggr);
            setConditions([]);
        } else {
            setSelectedPath(undefined);
            setSelectedPathName(undefined);
            setSelectedAggregation(undefined);
            setConditions([]);
        }
    }

    function onPathErased() {
        setSelectedPath(undefined);
        setSelectedPathName(undefined);
        setSelectedAggregation(undefined);
        setConditions([]);
    }

    function onModeChange(mode: FunctionMode) {
        setFunctionMode(mode);
        setSelectedPath(undefined);
        setSelectedPathName(undefined);
        setSelectedAggregation(undefined);
        setConditions([]);
    }

    function addCondition() {
        setConditions(prev => [...prev, {
            id: Math.random().toString(36).slice(2),
            leftFieldKey: destFields[0]?._key ?? null,
            operator: 'EQUAL',
            rightType: 'value',
            rightValue: '',
        }]);
    }

    // AGGR: relational path tree from the current object
    // LOOKUP: flat tree — all objects + their direct fields, no traversal
    const pathTree: TreeDataItem | TreeDataItem[] | null = useMemo(() => {
        if (functionMode === LookupFunctionMode) {
            return buildLookupTree(objectInfos, sharedObjectInfos, onPathClick);
        }
        if (selectedObjectInfos) {
            return buildPathTree(objectInfos, sharedObjectInfos, selectedObjectInfos.object._key, onPathClick);
        }
        return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [functionMode, objectInfos, sharedObjectInfos, selectedObjectInfos]);

    const treeData: TreeDataItem[] = pathTree
        ? Array.isArray(pathTree) ? pathTree : [pathTree]
        : [];

    return (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal>
            <PopoverTrigger className={TW_FieldSelectorButton}>
                <Plus className="mr-2 h-4 w-4" />
                {t("field.formula.select.aggregation.placeholder")}
            </PopoverTrigger>
            <PopoverContent className="w-[440px] p-4 flex flex-col gap-3 max-h-[90vh]" align="start">
                {/* Mode toggle — always visible */}
                <div className="flex gap-1 w-full shrink-0">
                    <Button
                        size="sm"
                        className={"flex-1 py-1 border-2 " + (functionMode === AggrFunctionMode ? " border-gray-500" : " border-transparent")}
                        onClick={() => onModeChange(AggrFunctionMode)}
                        {...useHoverAggrButton}
                    >
                        {t("field.formula.select.aggregation.mode.aggr")}
                    </Button>
                    <Button
                        size="sm"
                        className={"flex-1 py-1 border-2 " + (functionMode === LookupFunctionMode ? " border-gray-500" : " border-transparent")}
                        onClick={() => onModeChange(LookupFunctionMode)}
                        {...useHoverLookupButton}
                    >
                        {t("field.formula.select.aggregation.mode.lookup")}
                    </Button>
                </div>

                {/* Scrollable section */}
                <div className="flex flex-col gap-3 overflow-y-auto min-h-0 flex-1 w-full" style={scrollStyle}>
                    {/* Path selector */}
                    {treeData.length > 0 && <>
                        <Label className={TW_FormulaAggregationLabel}>{t("field.formula.select.aggregation.path.label")}</Label>
                        {!selectedPath && <TreeView searchable data={treeData} className={TW_FormulaAggregationPathSelectorList} style={scrollStyle} />}
                        {selectedPath &&
                            <Command className={TW_FormulaAggregationPathSelector}>
                                <CommandItem className={TW_FormulaAggregationPathSelectorItemSelected} onSelect={onPathErased}>
                                    <span className={TW_FormulaAggregationPathSelectorItemSelectedName}>{selectedPathName}</span>
                                    <ChevronDown className={TW_FormulaAggregationPathSelectorItemSelectedArrow} />
                                </CommandItem>
                            </Command>
                        }
                    </>}

                    {/* Function selector */}
                    <>
                        <Label className={TW_FormulaAggregationLabel}>{t("field.formula.select.aggregation.aggregation.label")}</Label>
                        <Select value={selectedAggregation ?? ""} onValueChange={setSelectedAggregation} disabled={!selectedPath || currentAggregationOptions.length === 0}>
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder={t("field.formula.select.aggregation.aggregation.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                {currentAggregationOptions.map(option => (
                                    <SelectItem key={option} value={option}>
                                        {option === LOOKUP_FIRST
                                            ? t("field.formula.select.aggregation.first")
                                            : t(`field.aggregation.label.${option}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>

                    {/* Conditions section */}
                    {selectedPath && <>
                        <Label className={TW_FormulaAggregationLabel}>{t("field.formula.select.aggregation.conditions.label")}</Label>
                        <div className="w-full flex flex-col gap-2">
                            {conditions.map((cond, index) => (
                                <div key={cond.id}>
                                    {index > 0 && (
                                        <div className="text-xs text-muted-foreground px-1">AND</div>
                                    )}
                                    <ConditionRowEditor
                                        row={cond}
                                        destFields={destFields}
                                        currentFields={currentObjectFields}
                                        onChange={(updated) => setConditions(prev => prev.map(c => c.id === cond.id ? { ...c, ...updated } : c))}
                                        onDelete={() => setConditions(prev => prev.filter(c => c.id !== cond.id))}
                                    />
                                </div>
                            ))}
                        </div>
                        <Button variant="outline" size="sm" className="w-full" onClick={addCondition} disabled={destFields.length === 0}>
                            <Plus className="mr-1 h-3 w-3" />
                            {t("field.formula.select.aggregation.add.condition")}
                        </Button>
                    </>}
                </div>

                {/* Action buttons — always visible */}
                <div className={TW_ButtonList + " w-full shrink-0"}>
                    <Button className={TW_AppDialogButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
                    <Button className={TW_AppDialogButton + (isSubmitDisabled ? " opacity-50 cursor-not-allowed" : "")} type="submit" onClick={onSubmit} {...confirmHover} disabled={isSubmitDisabled}>{t("general.button.ok.text")}</Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

interface ConditionRowEditorProps {
    row: ConditionRow;
    destFields: Field[];     // left operand: destination object fields
    currentFields: Field[];  // right 'field' mode: current object fields
    onChange: (updated: Partial<ConditionRow>) => void;
    onDelete: () => void;
}

function ConditionRowEditor({ row, destFields, currentFields, onChange, onDelete }: ConditionRowEditorProps) {
    const { t } = useTranslation();
    const [fieldOpen, setFieldOpen] = useState(false);
    const [fieldSearch, setFieldSearch] = useState('');

    const leftField = row.leftFieldKey ? destFields.find(f => f._key === row.leftFieldKey) ?? null : null;
    const validOperators = getValidOperator(leftField?.type, leftField?.typeItems);
    const noValueNeeded = row.operator === IS_EMPTY || row.operator === IS_NOT_EMPTY;

    const filteredDestFields = destFields.filter(f => matchesSearch(f.name, fieldSearch));

    const onLeftFieldChange = (fieldKey: string) => {
        const field = destFields.find(f => f._key === fieldKey);
        const ops = getValidOperator(field?.type, field?.typeItems);
        onChange({
            leftFieldKey: fieldKey,
            operator: ops.includes(row.operator) ? row.operator : (ops[0] ?? 'EQUAL' as Operator),
            rightValue: '',
        });
        setFieldOpen(false);
        setFieldSearch('');
    };

    const onRightModeToggle = (mode: ConditionRightType) => {
        onChange({ rightType: row.rightType === mode ? 'value' : mode, rightValue: '' });
    };

    return (
        <div className="flex flex-col gap-1.5 p-2 border rounded-md w-full">
            {/* Row 1: searchable field combobox + operator + delete */}
            <div className="flex gap-1 items-center">
                <Popover open={fieldOpen} onOpenChange={setFieldOpen}>
                    <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" role="combobox"
                            className="flex-1 h-8 justify-between text-xs font-normal overflow-hidden">
                            {leftField
                                ? <span className="truncate">{leftField.name}</span>
                                : <span className="text-muted-foreground truncate">{t("field.formula.select.aggregation.condition.field.placeholder")}</span>}
                            <ChevronDown className="h-3 w-3 opacity-50 ml-1 shrink-0" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-0" align="start">
                        <Command shouldFilter={false}>
                            <CommandInput
                                placeholder={t("field.formula.select.aggregation.condition.field.placeholder")}
                                value={fieldSearch}
                                onValueChange={setFieldSearch}
                                className="h-8 text-xs"
                            />
                            <CommandList>
                                <CommandEmpty className="py-2 text-center text-xs">{t("field.formula.no.fields.found")}</CommandEmpty>
                                <CommandGroup>
                                    {filteredDestFields.map(f => (
                                        <CommandItem key={f._key} value={f._key}
                                            onSelect={() => onLeftFieldChange(f._key)}
                                            className="text-xs">
                                            {f.name}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>

                <Select value={row.operator} onValueChange={(v) => onChange({ operator: v as Operator, rightValue: '' })} disabled={!leftField}>
                    <SelectTrigger className="w-28 h-8 text-xs shrink-0">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {validOperators.map(op => (
                            <SelectItem key={op} value={op} className="text-xs">{getOperatorLabel(op, t)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={onDelete}>
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            </div>

            {/* Row 2: right-side input + mode toggle buttons (hidden for IS_EMPTY / IS_NOT_EMPTY) */}
            {!noValueNeeded && (
                <div className="flex gap-1 items-center">
                    {row.rightType === 'value' && (
                        <div className="flex-1 min-w-0">
                            <PrimitiveSelect
                                pathKey={leftField?._key ?? null}
                                field={leftField}
                                value={parseConditionValue(row.rightValue, leftField?.type)}
                                disabled={!leftField}
                                onValueChange={(v) => onChange({ rightValue: v?.toString() ?? '' })}
                            />
                        </div>
                    )}

                    {row.rightType === 'field' && (
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" role="combobox"
                                    className="flex-1 h-8 justify-between text-xs font-normal overflow-hidden">
                                    {currentFields.find(f => f._key === row.rightValue)
                                        ? <span className="truncate">{currentFields.find(f => f._key === row.rightValue)!.name}</span>
                                        : <span className="text-muted-foreground truncate">{t("field.formula.select.aggregation.condition.field.placeholder")}</span>}
                                    <ChevronDown className="h-3 w-3 opacity-50 ml-1 shrink-0" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-56 p-0" align="start">
                                <Command filter={(value, search) => matchesSearch(value, search) ? 1 : 0}>
                                    <CommandInput placeholder={t("field.formula.select.aggregation.condition.field.placeholder")} className="h-8 text-xs" />
                                    <CommandList>
                                        <CommandEmpty className="py-2 text-center text-xs">{t("field.formula.no.fields.found")}</CommandEmpty>
                                        <CommandGroup>
                                            {currentFields.map(f => (
                                                <CommandItem key={f._key} value={f.name}
                                                    onSelect={() => onChange({ rightValue: f._key })}
                                                    className="text-xs">
                                                    {f.name}
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    )}

                    {row.rightType === 'formula' && (
                        <Input
                            className="flex-1 h-8 text-xs"
                            placeholder={t("field.formula.select.aggregation.condition.formula.placeholder")}
                            value={row.rightValue}
                            onChange={(e) => onChange({ rightValue: e.target.value })}
                        />
                    )}

                    <Button variant={row.rightType === 'field' ? 'default' : 'outline'}
                        size="sm" className="h-8 w-8 p-0 shrink-0"
                        title={t("field.formula.select.aggregation.condition.right.field")}
                        onClick={() => onRightModeToggle('field')}>
                        <Link2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant={row.rightType === 'formula' ? 'default' : 'outline'}
                        size="sm" className="h-8 w-8 p-0 shrink-0"
                        title={t("field.formula.select.aggregation.condition.right.formula")}
                        onClick={() => onRightModeToggle('formula')}>
                        <Braces className="h-3.5 w-3.5" />
                    </Button>
                </div>
            )}
        </div>
    );
}